/**
 * W1-11b (app-shell-ui): error boundaries (ARCHITECTURE.md §3.2, §15; DESIGN.md §2.2 "everything is saved").
 *
 *  - `variant="app"`: the crash screen. Work is safe up to the last committed change (the journal), so the screen
 *    says so and offers Reload, Go to the Library, "Download recovery data" (the open project's raw snapshot and
 *    journal from the persist client, plus a text error report) and "Copy error details".
 *  - `variant="panel"`: one feature panel failed; a compact notice with "Reload the panel" replaces it and the rest of
 *    the editor keeps working (optional services fail into placeholders, never into a blank app).
 *  - `variant="silent"`: renders nothing on error and reports it (dialogs: the host resolves them with null).
 * Strings come from `services.i18n` when the services exist, else from the i18n runtime.
 */

import { Component, useState, type ErrorInfo, type ReactNode } from 'react';
import type { MessageRef } from '../../core/util/types';
import { i18n as runtimeI18n } from '../../i18n/runtime';
import type { I18n } from '../../i18n/types';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import type { AppServices } from '../registry/types';
import { downloadBlob, errorReport, errorText, extensionFor, fileStamp, type ReportLabels } from './download';
import { m } from './shell.messages';
import s from './Shell.module.css';

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly variant: 'app' | 'panel' | 'silent';
  readonly services?: AppServices | null;
  /** Called once per caught error (after logging). */
  readonly onError?: (error: unknown) => void;
  /** Changing it clears the error (e.g. a new route). */
  readonly resetKey?: unknown;
  /** Panel name for the report. */
  readonly label?: string;
}

interface State {
  readonly error: unknown;
  readonly failed: boolean;
  readonly componentStack: string | null;
  readonly attempt: number;
}

function textOf(services: AppServices | null | undefined): I18n {
  if (services === null || services === undefined) return runtimeI18n;
  try {
    return services.i18n;
  } catch {
    return runtimeI18n;
  }
}

/** Error report labels in the UI language. */
export function reportLabels(t: I18n): ReportLabels {
  return {
    componentStack: () => t.t(m.reportComponentStack),
    url: (url) => t.t(m.reportUrl, { url }),
    browser: (agent) => t.t(m.reportBrowser, { agent }),
    time: (time) => t.t(m.reportTime, { time }),
  };
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null, failed: false, componentStack: null, attempt: 0 };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(`shell: ${this.props.label ?? this.props.variant} crashed`, error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
    this.props.onError?.(error);
  }

  override componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, failed: false, componentStack: null });
    }
  }

  private readonly retry = (): void => {
    this.setState((st) => ({ error: null, failed: false, componentStack: null, attempt: st.attempt + 1 }));
  };

  override render(): ReactNode {
    const { variant, services, children, label } = this.props;
    if (!this.state.failed) {
      return variant === 'panel' ? <PanelKey key={this.state.attempt}>{children}</PanelKey> : children;
    }
    if (variant === 'silent') return null;
    const report = errorReport(this.state.error, this.state.componentStack, reportLabels(textOf(services)));
    if (variant === 'panel') {
      return <PanelCrash services={services ?? null} report={report} onRetry={this.retry} label={label ?? null} />;
    }
    return <CrashScreen services={services ?? null} error={this.state.error} report={report} />;
  }
}

/** Remounts the panel subtree on retry (key changes). */
function PanelKey(props: { readonly children: ReactNode }) {
  return props.children;
}

/** Copies text to the system clipboard; resolves whether it worked. */
async function copyText(text: string): Promise<boolean> {
  const clipboard = (navigator as { readonly clipboard?: Clipboard }).clipboard;
  if (clipboard === undefined) return false;
  try {
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function PanelCrash(props: {
  readonly services: AppServices | null;
  readonly report: string;
  readonly onRetry: () => void;
  readonly label: string | null;
}) {
  const t = textOf(props.services);
  return (
    <div className={s.panelCrash} role="alert" data-crashed-panel={props.label ?? undefined}>
      <Icon name="TriangleAlert" size={20} />
      <p className={s.panelCrashTitle}>{t.t(m.panelCrashTitle)}</p>
      <p className={s.panelCrashBody}>{t.t(m.panelCrashBody)}</p>
      <div className={s.crashActions}>
        <Button variant="primary" icon="RotateCcw" onClick={props.onRetry}>
          {t.t(m.panelRetry)}
        </Button>
        <Button
          variant="ghost"
          icon="Copy"
          onClick={() => {
            void copyText(props.report).then((ok) => {
              if (ok) props.services?.ui.toast({ key: m.toastCopied }, { kind: 'success', durationMs: 2500 });
            });
          }}
        >
          {t.t(m.crashCopy)}
        </Button>
      </div>
    </div>
  );
}

function projectIdOf(services: AppServices | null): string | null {
  if (services === null) return null;
  try {
    return services.store?.projectId ?? services.editor.getState().projectId;
  } catch {
    return null;
  }
}

/** Downloads the project's recovery data (if any) and the error report. */
async function downloadRecovery(services: AppServices | null, report: string): Promise<MessageRef | null> {
  downloadBlob(new Blob([report], { type: 'text/plain' }), `phiedit-error-${fileStamp()}.txt`);
  const id = projectIdOf(services);
  if (services === null || id === null) return null;
  try {
    const blob = await services.persist.recoveryData(id as Parameters<AppServices['persist']['recoveryData']>[0]);
    downloadBlob(blob, `phiedit-recovery-${id}-${fileStamp()}.${extensionFor(blob.type)}`);
    return null;
  } catch (err) {
    return { key: m.toastRecoveryFailed, params: { error: errorText(err, textOf(services).t(m.unknownError)) } };
  }
}

export function CrashScreen(props: {
  readonly services: AppServices | null;
  readonly error: unknown;
  readonly report: string;
  readonly title?: string;
  readonly body?: string;
}) {
  const t = textOf(props.services);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  return (
    <main className={s.crash} role="alert" aria-labelledby="phiedit-crash-title">
      <div className={s.crashCard}>
        <Icon name="OctagonAlert" size={32} />
        <h1 id="phiedit-crash-title" className={s.crashTitle}>
          {props.title ?? t.t(m.crashTitle)}
        </h1>
        <p className={s.crashBody}>{props.body ?? t.t(m.crashBody)}</p>
        <div className={s.crashActions}>
          <Button
            variant="primary"
            icon="RotateCcw"
            onClick={() => {
              location.reload();
            }}
          >
            {t.t(m.crashReload)}
          </Button>
          <Button
            variant="secondary"
            icon="Download"
            onClick={() => {
              void downloadRecovery(props.services, props.report).then((r) => {
                setProblem(r === null ? null : t.tr(r));
              });
            }}
          >
            {t.t(m.crashDownload)}
          </Button>
          <Button
            variant="secondary"
            icon="LibraryBig"
            onClick={() => {
              location.hash = '#/';
              location.reload();
            }}
          >
            {t.t(m.crashLibrary)}
          </Button>
          <Button
            variant="ghost"
            icon={copied ? 'Check' : 'Copy'}
            onClick={() => {
              void copyText(props.report).then(setCopied);
            }}
          >
            {t.t(m.crashCopy)}
          </Button>
        </div>
        {problem !== null && <p className={s.crashProblem}>{problem}</p>}
        <details className={s.crashDetails}>
          <summary>{t.t(m.crashDetails)}</summary>
          <pre>{props.report}</pre>
        </details>
      </div>
    </main>
  );
}
